import { InjectionToken } from "@angular/core";

export type ClassroomConfig = {
    /** If true, the client will listen to slide/classroom commands on the SignalR stream. */
    isConsumer: boolean;

    /** If true, the client is allowed to send changes to consumers. */
    isEmitter: boolean;

    // NOTE: `emitPrimarySlideChanges` / `emitSecondarySlideChanges` were removed in
    // Phase 5 — Phase 3 had already migrated all primary/secondary slide writes to
    // direct `ClassroomFacade.showSlide(...)` calls, so these per-layer
    // SignalR-emit flags became dead code. Role asymmetry for primary writes is
    // now enforced at the call site (only Teacher dispatchers exist for primary
    // slide selection) and by `LessonYjsService.guardWrite()` (Teacher + Beamer only).

    /** If true, slide mutations (drawings) are sent to all clients in the classroom. */
    emitSlideMutations?: boolean;

    /** If true, clicks on hotspots are sent to the teacher so that the teacher can activate the hotspot internally. */
    emitHotspotClicks?: boolean;

    /**
     * If true, clicks on the toolbar undo/redo buttons are not directly processed but sent to the teacher via SignalR.
     * UPDATE: also used from "navigateToParentLectionOrChapter" as these are all some kind of navigation effects.
     */
    emitUndoRedo: boolean;

    /** True for all screen types which can be seen by students. */
    isPresentationScreen: boolean;

    /**
     * If true, modifications/interactions on HTML elements will be detected, added to component outputs and be sent via SignalR to the classroom.
     * If false, modifications will be ignored.
     */
    emitHtmlElementModifications: boolean;

    /**
     * Emit video changes/events,
     * e.g. paused, seeked etc.
     * loaded event is handled separately.
     */
    emitVideoChanges: boolean;

    /**
     * Emit video loaded events.
     * Only the teacher should emit loaded events.
     */
    emitVideoLoaded: boolean;

    /**
     * Emit video changes event stream,
     * e.g. loaded, paused, seeked etc.
     */
    consumeVideoChanges: boolean;

    /**
     * Show/hide the toolbar.
     * Toolbar shown be shown only on teacher an beamer screens.
     */
    showToolbar: boolean;

    /**
     * If fixed, the toolbar will be attached to the left and centered vertically.
     * The toolbar can overlap contents of the current slide.
     */
    useFixedToolbar?: boolean;

    /**
     * If true, the shown element cannot be controlled by the user.
     * This means:
     * - a video cannot be paused|played|seeked
     * - HTML elements and rubber elements cannot be interacted with
     */
    disableElementUsage?: boolean;
};
export const PCP_CLASSROOM_SYNC_CONFIG =
    new InjectionToken<ClassroomConfig>('PCP_CLASSROOM_SYNC_CONFIG');
